import type { DayOfWeek } from 'app/typings/core/admin/enum/dayOfWeek';
import { DAY_OF_WEEK } from 'app/typings/core/admin/enum/dayOfWeek';
import { OPENING_CONFIGURATION_TYPE } from 'app/typings/core/admin/experiences/enum/openingConfiguration';

type MaxBookingConstantConfiguration = {
  type: OPENING_CONFIGURATION_TYPE.CONSTANT;
  configuration: {
    maxBooking?: number | null;
  } | null;
};

type MaxBookingDailyConfiguration = {
  type: OPENING_CONFIGURATION_TYPE.DAILY;
  days: Record<
    DayOfWeek,
    {
      maxBooking?: number | null;
    }
  >;
};

export type UpdatePackConfigurationRequest =
  | MaxBookingConstantConfiguration
  | MaxBookingDailyConfiguration;

type ConstantConfigurationInput = {
  type: OPENING_CONFIGURATION_TYPE.CONSTANT;
  configuration: {
    maxBooking: number;
  };
};

type DailyConfigurationInput = {
  type: OPENING_CONFIGURATION_TYPE.DAILY;
  days: Partial<
    Record<
      DayOfWeek,
      {
        isToggled: boolean;
        maxBooking: number;
      }
    >
  >;
};

export type MaxBookingConfigurationInput = { isToggled: boolean } & (
  | ConstantConfigurationInput
  | DailyConfigurationInput
);

export const mapToMaxBookingConfiguration = (
  data: MaxBookingConfigurationInput
): UpdatePackConfigurationRequest | null => {
  if (data.isToggled === false) {
    return null;
  }

  if (data.type === OPENING_CONFIGURATION_TYPE.CONSTANT) {
    return mapToMaxBookingConstantConfiguration(data);
  }

  return mapToMaxBookingDailyConfiguration(data);
};

const mapToMaxBookingConstantConfiguration = (
  data: ConstantConfigurationInput
): UpdatePackConfigurationRequest => {
  const maxBooking = data.configuration.maxBooking ?? 0;

  return {
    type: OPENING_CONFIGURATION_TYPE.CONSTANT,
    configuration:
      maxBooking > 0
        ? {
            maxBooking,
          }
        : null,
  };
};

const mapToMaxBookingDailyConfiguration = (
  data: DailyConfigurationInput
): UpdatePackConfigurationRequest => {
  const allDays = Object.values(DAY_OF_WEEK);
  const daysConfig = allDays.reduce((acc, day) => {
    const dayData = data.days[day];

    acc[day] = {
      maxBooking: dayData?.isToggled ? dayData.maxBooking : null,
    };

    return acc;
  }, {} as Record<DayOfWeek, { maxBooking?: number | null }>);

  return {
    type: OPENING_CONFIGURATION_TYPE.DAILY,
    days: daysConfig,
  };
};
